import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { obterIdentificacao } from '../api/configuracao-identificacao.js';
import { useConsulta } from '../api/useConsulta.js';
import { usePode } from '../autorizacao/usePode.js';
import { Abas, PainelDeAba } from '../componentes/Abas.js';
import { AvisoDaTela } from '../componentes/AvisoDaTela.js';
import { Cartao } from '../componentes/Cartao.js';
import { EstadoVazio } from '../componentes/EstadoVazio.js';
import { IconeEtiqueta } from '../componentes/Icones.js';
import { Pagina } from '../componentes/Pagina.js';
import { GeradorDeEtiquetas } from './etiquetas/GeradorDeEtiquetas.js';
import { GeradorDeEtiquetasDeLote } from './etiquetas/GeradorDeEtiquetasDeLote.js';
import './Etiquetas.css';

const ABAS = [
  { id: 'lote', rotulo: 'Por lote' },
  { id: 'produto', rotulo: 'Produto sem lote' },
] as const;

type IdDaAba = (typeof ABAS)[number]['id'];

const GRUPO_DAS_ABAS = 'etiquetas';

/**
 * Com o armazém identificando por LOTE, a tela abre na etiqueta de lote e
 * mantém a de produto numa aba — produto que não controla lote continua com
 * a etiqueta por produto. Só um gerador fica montado por vez: cada um tem a
 * própria folha de impressão, e duas folhas imprimiriam as duas listas.
 */
function PorModo({ modo }: { readonly modo: 'PRODUTO' | 'LOTE' }) {
  // O link de outra tela (a aba Produtos da sincronização) chega com
  // `?aba=lote&busca=<codPro>`: abre na aba e já busca os lotes do produto.
  const [parametros] = useSearchParams();
  const [aba, definirAba] = useState<IdDaAba>(
    parametros.get('aba') === 'produto' ? 'produto' : 'lote',
  );
  const buscaInicial = parametros.get('busca')?.trim() ?? '';

  if (modo === 'PRODUTO') {
    return <GeradorDeEtiquetas />;
  }

  return (
    <div className="etiquetas__abas">
      <Abas
        rotulo="Tipo de etiqueta"
        abas={ABAS}
        ativa={aba}
        aoTrocar={(id) => {
          definirAba(id === 'produto' ? 'produto' : 'lote');
        }}
        grupo={GRUPO_DAS_ABAS}
      />
      <PainelDeAba id={aba} grupo={GRUPO_DAS_ABAS}>
        {aba === 'lote' ? (
          <GeradorDeEtiquetasDeLote buscaInicial={buscaInicial} />
        ) : (
          <GeradorDeEtiquetas />
        )}
      </PainelDeAba>
    </div>
  );
}

function Gerador() {
  const consulta = useConsulta(obterIdentificacao);

  if (consulta.erro !== null) {
    // Sem saber o modo, a etiqueta de produto continua disponível: é a que
    // vale para todo armazém, e travar a impressão por uma consulta de
    // configuração pararia quem só precisa etiquetar um produto.
    return (
      <>
        <AvisoDaTela tom="atencao">
          {`Não deu para saber se o armazém identifica por lote (${consulta.erro}). As etiquetas abaixo são por produto.`}
        </AvisoDaTela>
        <GeradorDeEtiquetas />
      </>
    );
  }

  if (consulta.dados === null) {
    return (
      <p className="etiquetas__explicacao" role="status">
        Consultando como o armazém identifica os itens…
      </p>
    );
  }

  return <PorModo modo={consulta.dados.modoIdentificacaoQr} />;
}

/**
 * Etiquetas com o QR da plataforma (qr-code.md §14 e §15).
 *
 * Na fase de testes é a plataforma que gera o QR, e o cliente só imprime:
 * assim a matriz da etiqueta é, por construção, a que o backend usa para
 * reconhecer a foto no inventário. O QR vem do middleware
 * (`GET /produtos/qrcode`, `GET /lotes/qrcode`), nunca de um gerador local —
 * dois geradores podem escolher máscaras diferentes para o mesmo texto.
 *
 * O que o QR carrega segue a identificação do armazém (Configurações): código
 * do produto ou do lote.
 *
 * Administrador e Supervisor, como o endpoint. Para o perfil Consulta a tela
 * explica quem gera, sem ferramenta nenhuma à mostra.
 */
export function Etiquetas() {
  const pode = usePode();

  return (
    <Pagina
      contexto="Administração"
      titulo="Etiquetas"
      faixa={
        <p className="etiquetas__explicacao">
          O QR que o coletor lê na contagem, com o código em fonte grande abaixo. Escolha os itens,
          confira e imprima.
        </p>
      }
    >
      {pode('etiquetas.imprimir') ? (
        <Gerador />
      ) : (
        <Cartao semRespiro>
          <EstadoVazio
            tom="neutro"
            icone={<IconeEtiqueta tamanho={22} />}
            titulo="Etiquetas de produto são geradas por Administrador ou Supervisor."
            detalhe="Peça a quem responde pelo armazém: a etiqueta é o que o coletor vai ler no inventário."
          />
        </Cartao>
      )}
    </Pagina>
  );
}
